import { track } from '@amplitude/analytics-browser'
import { DocumentNode } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import {
  PerformanceGoalsCreateCycleDocument,
  PerformanceGoalsCreateCycleMutationVariables
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { validateDate, validateTitle } from 'modules/performance_cycles/lib/cyclesValidations'

export const CreateCycleModal: React.FC<{
  onClose: () => void
  refetchQueries?: DocumentNode[]
}> = ({ onClose, refetchQueries = [] }) => {
  const { company } = useCurrentSession()

  const [createCycle] = useMutation(PerformanceGoalsCreateCycleDocument, {
    extractorFn: (d) => d.performanceCyclesMutations.createPerformancecyclesCycle,
    refetchQueries,
    awaitRefetchQueries: true
  })

  const form = useForm<Omit<PerformanceGoalsCreateCycleMutationVariables, 'companyId'>>({
    defaultValues: {
      startOn: moment().startOf('month').format(BACKEND_DATE_FORMAT),
      endOn: moment().endOf('month').format(BACKEND_DATE_FORMAT),
      title: ''
    },
    validate(values) {
      const errors: FormErrors<PerformanceGoalsCreateCycleMutationVariables> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      const dateError = validateDate(values.startOn, values.endOn)
      if (dateError) {
        errors.endOn = dateError
      }

      return errors
    },
    onSubmit: async (values) => {
      const companyId = company.id

      if (!companyId) throw 'Missing company id'

      const variables: PerformanceGoalsCreateCycleMutationVariables = {
        ...values,
        companyId
      }

      const result = await createCycle({
        variables: variables
      })

      const handled = handleResult(result, () => null)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully')
        })

        track(trackingEvents.performanceCycles.createCycle)
        onClose()
      }

      return handled
    }
  })

  return (
    <Modal
      title={i18n.t('performance_cycles.new_cycle_modal.title')}
      onClose={onClose}
      illustration="time-planning"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        <TextField
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.create_key_results_modal.fields.title')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <Box flexDirection="row" gap="s12">
          <DayPicker
            value={form.values.startOn}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.start_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, startOn: date })
            }}
          />
          <DayPicker
            value={form.values.endOn}
            error={form.errors.endOn?.toString()}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.end_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, endOn: date })
            }}
          />
        </Box>
      </Box>
    </Modal>
  )
}
